import { styled } from '../../../theme/stitches.config';
import { SMPLinkWrapper } from './SMPLinkWrapper';
import { SMPSportEntity } from './SMPSportEntity';

export const SMPFlex = styled.withConfig({ componentId: 'CommonWidget', displayName: 'SMPFlex' })('div', {
  boxSizing: 'border-box',
  display: 'flex',
  '&.filtersRow': {
    fd: 'row',
    position: 'relative',
    py: '$2_5',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  '&.filtersColumn': {
    fd: 'column',
    pt: '$2_5',
    paddingLeft: '$3',
    position: 'relative',
  },
  variants: {
    grid: {
      true: {
        display: 'grid !important',
        gridTemplateColumns: 'minmax(0, 1fr) auto',

        [`& > ${SMPLinkWrapper} > :first-child`]: {
          justifyContent: 'start',
        },
      },
    },
    align: {
      start: {
        alignItems: 'flex-start',
      },
      center: {
        alignItems: 'center !important',
      },
      end: {
        alignItems: 'flex-end',
      },
      stretch: {
        alignItems: 'stretch',
      },
    },
    justify: {
      start: {
        justifyContent: 'flex-start !important',
      },
      center: {
        justifyContent: 'center',
      },
      end: {
        justifyContent: 'flex-end',
      },
      stretch: {
        justifyContent: 'stretch',
      },
      spaceBetween: {
        justifyContent: 'space-between !important',
      },
      spaceAround: {
        justifyContent: 'space-around',
      },
      spaceEvenly: {
        justifyContent: 'space-evenly',
      },
    },
    wrap: {
      wrap: {
        flexWrap: 'wrap',
      },
      noWrap: {
        flexWrap: 'nowrap',
      },
      wrapReverse: {
        flexWrap: 'wrap-reverse',
      },
    },
    direction: {
      row: {
        flexDirection: 'row',
      },
      column: {
        flexDirection: 'column !important',
      },
      rowReverse: {
        flexDirection: 'row-reverse',
      },
      columnReverse: {
        flexDirection: 'column-reverse',
      },
    },
    basis: {
      auto: { flexBasis: 'auto' },
    },
    grow: {
      0: { flexGrow: 0 },
      10: { flexGrow: 0.2 },
      1: { flexGrow: 1 },
      2: { flexGrow: 2 },
      3: { flexGrow: 3 },
      4: { flexGrow: 4 },
      5: { flexGrow: 5 },
    },
    shrink: {
      1: { flexShrink: 1 },
      2: { flexShrink: 2 },
      3: { flexShrink: 3 },
      4: { flexShrink: 4 },
      5: { flexShrink: 5 },
    },
    gap: {
      none: {
        gap: 0,
      },
      sm: {
        gap: '$1',
      },
      md: {
        gap: '$2',
      },
      lg: {
        gap: '$3',
      },
      xl: {
        gap: '$4',
      },
      xxl: {
        gap: '$5',
      },
    },
    display: {
      flex: {
        display: 'flex',
      },
      inline: {
        display: 'inline-flex',
      },
      none: {
        display: 'none',
      },
    },
    outlined: {
      true: {
        border: '1px solid $orange500',
      },
    },
    tennisEventInfo: {
      small: {},
      default: {
        py: '0',

        '& > *': {
          py: '$3',

          '&:nth-last-child(2)': {
            borderRight: '1px solid $primaryBackgroundColor',
          },
        },
      },
    },
    divider: {
      top: {
        borderTop: '1px solid $dividerBorderColor',
      },
      bottom: {
        borderBottom: '1px solid $dividerBorderColor',
      },
    },
    form: {
      true: {},
      false: {},
    },
    variant: {
      base: {
        fd: 'row',
        ai: 'normal',
        jc: 'center',
      },
      score: {
        ai: 'center',
        fd: 'column',
        textAlign: 'center',
      },
      odds1X2: {
        fd: 'row',
        ai: 'center',
        jc: 'center',
        gap: '$2',
      },
      odds12Vertical: {
        fd: 'column',
        gap: '$1',

        '& > *': {
          width: '100px',
        },
      },
      odd: {
        flex: '1 1 auto',
        bc: '$oddItemPrimaryBgColor',
        br: '2px',
        px: '$2',
        py: '7px',
        '&:hover': {
          bc: '$accentPrimaryBgColor',
          cursor: 'pointer',
        },
      },
      tournament: {
        fd: 'row',
        jc: 'space-between',
      },
      livescoreMatch: {
        py: '$3',
        pr: '$3',
        [`& > ${SMPLinkWrapper} > div:first-child > :first-child`]: {
          padding: '$3',
        },
        [`& > div:first-child > ${SMPLinkWrapper} > :first-child`]: {
          padding: '$3',
        },
      },
      tabsContainer: {
        overflow: 'auto',
        whiteSpace: 'nowrap',
        scrollBehavior: 'smooth',
        // The following lines allow side scrolling while the scrollbar is hidden
        // Chrome/Safari/Webkit
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        // Firefox
        scrollbarWidth: 'none',
        // IE 10+
        msOverflowStyle: 'none',
        '& svg:first-child': {
          width: '10px',
          position: 'absolute',
          left: '6px',
          cursor: 'pointer',
          transform: 'translateY(5px)',
        },
        '& svg:last-child': {
          width: '10px',
          position: 'absolute',
          right: '5px',
          cursor: 'pointer',
          transform: 'translateY(5px)',
        },
        '& svg:hover': {
          color: 'orange',
          cursor: 'pointer',
          transition: 'color 0.3s',
        },
        '& svg:active': {
          height: '14px',
          transition: 'height 0.3s',
          'transition-timing-function': 'ease-out',
        },
      },
      standingsMarginals: {
        fd: 'row',
        jc: 'start',
        ai: 'start',
        py: '$2_5',
        pr: '$4',
        pl: '$2_5',
      },
      header: {
        pt: '$2_5',
        px: '$3',
      },
      formWrapper: {
        fd: 'row-reverse',
        jc: 'start',
        gap: '$1',
      },
      column: {
        fd: 'column',
      },
      standingsFilters: {
        position: 'relative',
        fd: 'row',
        pt: '$2_5',
        pb: '$2_5',
        bc: '$rowBackgroundColor',
        '@bp0': {
          pl: '$3',
          pr: '$3',
        },
        '@bp3': {
          px: '$4',
        },
      },
      centeredRow: {
        fd: 'row',
        ai: 'center',
        jc: 'center',
      },
      fansUnitedContentWrapper: {
        fd: 'column',
        ai: 'center',
        borderTop: '2px solid $orange500',
        pt: '$2',
        pb: '$3',
        bc: '$fansUnitedContentPrimaryBgColor',
      },
      fansUnitedHeaderWrapper: {
        jc: 'space-between',
        ai: 'center',
        cursor: 'pointer',
        px: '$2',
        py: '11px',
      },
      fansUnitedOutcome1X2: {
        fd: 'column',
        ai: 'center',
        jc: 'center',
        gap: '$2',
        mt: '$2',
        width: '100%',
        '@bp1': {
          fd: 'row',
          ai: 'stretch',
          width: 'auto',
        },
      },
      fansUnited1X2Pill: {
        flex: '1 1 auto',
        bc: '$oddItemPrimaryBgColor',
        br: '$1',
        px: '$2',
        py: '$1',
        jc: 'center',
        width: '100%',
        '&:hover': {
          bc: '$fansUnitedPillHoverColor',
          cursor: 'pointer',
          '& > *': {
            color: '$accentPrimaryBgColor',
          },
        },
        '@bp1': {
          width: '115px',
        },
      },
      tennisParticipantsCol: {
        width: '100%',
        flexDirection: 'column',
      },
      tennisDoubleMatchMobileScore: {
        flexGrow: 'unset',
        alignItems: 'center',
        height: 'calc(100% - (100% - $2_5) / 2 - $2_5)',
        py: '$1',

        '& > div': {
          height: '100%',
        },
      },
      even: {
        '&:nth-child(even)': {
          background: '$tabRowBackgroundColor',
        },
      },
      showMore: {
        ai: 'center',
        '&:hover': {
          cursor: 'pointer',
        },
      },
      footballMatchesH2HFilters: {
        position: 'relative',
        fd: 'row',
        pt: '$2_5',
        pb: '$2_5',
        mt: '2px',
        mb: '4px',
        bc: '$rowBackgroundColor',
        '@bp0': {
          pl: '$3',
        },
        '@bp3': {
          px: '$4',
        },
      },
      eventInfo: {
        display: 'grid',
        gridTemplateColumns: '1fr minmax(20%, max-content) 1fr',
        alignItems: 'center',

        [`${SMPSportEntity}`]: {
          py: 0,
        },
      },
      gridGroupContrast: {
        backgroundColor: '$sportEntityContainerPrimaryBgColor',
        fd: 'row',
        jc: 'start',
        ai: 'start',
        py: '$2_5',
        pr: '$4',
        pl: '$2_5',
      },
    },
  },
  defaultVariants: {
    variant: 'base',
  },
  compoundVariants: [
    {
      variant: 'livescoreMatch',
      tennisEventInfo: 'default',
      css: {
        py: '0',

        [`& > ${SMPLinkWrapper} > div`]: {
          py: '$3',

          '&:first-child': {
            pl: '$2',

            '& > :first-child': {
              padding: '0',
            },
          },
          '&:last-child': {
            borderRight: '1px solid $primaryBackgroundColor',
          },
        },
      },
    },
    {
      variant: 'livescoreMatch',
      tennisEventInfo: 'small',
      css: {
        p: '0',

        [`& > ${SMPLinkWrapper} > div`]: {
          py: '$3',

          '&:first-child': {
            pl: '$2',

            '& > :first-child': {
              padding: '0',
            },
          },
          '&:last-child': {
            pr: '$3',
          },
        },
      },
    },
    {
      variant: 'tournament',
      grid: true,
      css: {
        gridTemplateColumns: 'minmax(0, 1fr) auto',

        '& > a > :first-child': {
          justifyContent: 'start',
        },
      },
    },
    {
      variant: 'livescoreMatch',
      form: true,
      grid: true,
      css: {
        gridTemplateColumns: 'minmax(0, 1fr) auto auto',

        [`& > ${SMPLinkWrapper}`]: {
          '& > :nth-child(2)': {
            pr: 'unset',
          },
          '& > :last-child': {
            pr: '$3',
          },
        },
      },
    },
    {
      variant: 'tennisDoubleMatchMobileScore',
      align: 'end',
      css: {
        alignItems: 'flex-end',
      },
    },
    {
      variant: 'tennisDoubleMatchMobileScore',
      divider: 'bottom',
      css: {
        pt: '0',
        height: 'calc((100% - $2_5) / 2 + $2_5 + 1px)',

        '& > div > a > div': {
          '& > :first-child': {
            height: '$2_5',
          },
          '& > :last-child': {
            height: 'calc(100% - $2_5)',
          },
        },
      },
    },
  ],
});
